<script setup>
/* global axios */
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const templates = ref([]);
const isLoading = ref(true);
const searchQuery = ref('');

const currentAccountId = useMapGetter('getCurrentAccountId');
const inboxes = useMapGetter('inboxes/getInboxes');

// Filtered templates based on search
const filteredTemplates = computed(() => {
  if (!searchQuery.value) return templates.value;
  const query = searchQuery.value.toLowerCase();
  return templates.value.filter(
    template =>
      template.name.toLowerCase().includes(query) ||
      (template.keyword && template.keyword.toLowerCase().includes(query))
  );
});

onMounted(async () => {
  await fetchTemplates();
});

// Watch route changes to refetch when returning to list page
watch(
  () => route.name,
  (newName, oldName) => {
    // Refetch when navigating back to list from editor
    if (
      newName === 'flex_templates_list' &&
      oldName !== 'flex_templates_list'
    ) {
      fetchTemplates();
    }
  }
);

const fetchTemplates = async () => {
  isLoading.value = true;
  try {
    const response = await axios.get(
      `/api/v1/accounts/${currentAccountId.value}/flex_templates`
    );
    templates.value = response.data;
  } catch (error) {
    console.error('Failed to fetch templates:', error);
  } finally {
    isLoading.value = false;
  }
};

const openEditor = (templateId = null) => {
  if (templateId) {
    router.push({
      name: 'flex_templates_edit',
      params: { templateId },
    });
  } else {
    router.push({ name: 'flex_templates_new' });
  }
};

const toggleActive = async template => {
  try {
    const response = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/flex_templates/${template.id}/toggle_active`
    );
    const updated = response.data;
    const index = templates.value.findIndex(t => t.id === template.id);
    if (index !== -1) {
      templates.value[index] = updated;
    }
    useAlert(
      updated.is_active
        ? t('FLEX_TEMPLATES.ACTIVATED')
        : t('FLEX_TEMPLATES.DEACTIVATED')
    );
  } catch (error) {
    useAlert(t('FLEX_TEMPLATES.TOGGLE_ERROR'));
  }
};

const deleteTemplate = async template => {
  if (!confirm(t('FLEX_TEMPLATES.DELETE_CONFIRM'))) return;

  try {
    await axios.delete(
      `/api/v1/accounts/${currentAccountId.value}/flex_templates/${template.id}`
    );
    templates.value = templates.value.filter(t => t.id !== template.id);
    useAlert(t('FLEX_TEMPLATES.DELETED'));
  } catch (error) {
    useAlert(t('FLEX_TEMPLATES.DELETE_ERROR'));
  }
};

const getInboxName = inboxId => {
  if (!inboxId) return t('FLEX_TEMPLATES.ACCOUNT_WIDE');
  const inbox = inboxes.value?.find(i => i.id === inboxId);
  return inbox?.name || `Inbox #${inboxId}`;
};
</script>

<template>
  <div class="settings-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="ws-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 56px"
    >
      <h1 class="text-xl font-semibold" style="color: #111827">
        {{ t('FLEX_TEMPLATES.HEADER') }}
      </h1>
      <button class="ws-btn" @click="openEditor()">
        <span class="i-ph-plus-bold size-4" />
        {{ t('FLEX_TEMPLATES.ADD_NEW') }}
      </button>
    </header>

    <!-- Description -->
    <div class="ws-desc px-6 py-3">
      <p class="text-sm" style="color: #4b5563; margin: 0">
        {{ t('FLEX_TEMPLATES.DESCRIPTION') }}
      </p>
    </div>

    <!-- Search -->
    <div class="ws-toolbar px-6 py-3">
      <div class="flex items-center gap-4">
        <div class="relative flex-1 max-w-sm">
          <span
            class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
            style="color: #9ca3af"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="ws-search reset-base w-full pl-10 pr-4 py-2 text-sm rounded-lg"
            placeholder="Search templates..."
          />
        </div>
        <span class="text-sm" style="color: #6b7280">{{ filteredTemplates.length }} templates</span>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-6">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <woot-loading-state :message="t('FLEX_TEMPLATES.LOADING')" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!filteredTemplates.length"
        class="flex flex-col items-center justify-center py-20"
      >
        <div class="i-lucide-layout-template text-4xl text-n-slate-11 mb-4" />
        <p class="text-n-slate-11">{{ t('FLEX_TEMPLATES.EMPTY') }}</p>
        <button class="primary-action-btn mt-4" @click="openEditor()">
          {{ t('FLEX_TEMPLATES.CREATE_FIRST') }}
        </button>
      </div>

      <!-- Templates Table -->
      <div v-else class="ws-table-wrap">
        <table class="ws-table">
          <thead class="ws-thead">
            <tr>
              <th class="ws-th">Status</th>
              <th class="ws-th">Name</th>
              <th class="ws-th">Keyword</th>
              <th class="ws-th">Inbox</th>
              <th class="ws-th">Mode</th>
              <th class="ws-th ws-th--right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="template in filteredTemplates"
              :key="template.id"
              class="ws-tr"
            >
              <td class="ws-td">
                <div
                  class="w-2.5 h-2.5 rounded-full"
                  :style="
                    template.is_active
                      ? 'background:#22c55e'
                      : 'background:#d1d5db'
                  "
                />
              </td>
              <td class="ws-td font-medium" style="color: #111827">
                {{ template.name }}
              </td>
              <td class="ws-td">
                <span
                  v-if="template.keyword"
                  class="flex items-center gap-1 text-sm"
                  style="color: #4b5563"
                >
                  <span class="i-lucide-hash size-3" />{{ template.keyword }}
                </span>
                <span v-else style="color: #9ca3af">-</span>
              </td>
              <td class="ws-td" style="color: #4b5563; font-size: 12px">
                {{ getInboxName(template.inbox_id) }}
              </td>
              <td class="ws-td">
                <div class="flex items-center gap-2">
                  <span
                    class="px-2 py-0.5 text-xs rounded-full font-medium"
                    :style="
                      template.template_mode === 'set'
                        ? 'background:#fff7ed;color:#004db3;border:1px solid #fed7aa'
                        : 'background:#f3f4f6;color:#374151;border:1px solid #e5e7eb'
                    "
                  >
                    {{ template.template_mode === 'set' ? 'Set' : 'Single' }}
                  </span>
                  <span
                    v-if="
                      template.template_mode === 'set' &&
                      template.rules_json?.length
                    "
                    class="text-xs"
                    style="color: #9ca3af"
                    >({{ template.rules_json.length }} rules)</span>
                </div>
              </td>
              <td class="ws-td ws-td--right">
                <div class="flex justify-end gap-1">
                  <Button
                    v-tooltip.top="
                      template.is_active
                        ? t('FLEX_TEMPLATES.DEACTIVATE')
                        : t('FLEX_TEMPLATES.ACTIVATE')
                    "
                    :icon="
                      template.is_active
                        ? 'i-lucide-toggle-right'
                        : 'i-lucide-toggle-left'
                    "
                    slate
                    xs
                    faded
                    :class="template.is_active ? 'text-green-500' : ''"
                    @click="toggleActive(template)"
                  />
                  <Button
                    v-tooltip.top="t('FLEX_TEMPLATES.EDIT')"
                    icon="i-ph-pencil-simple-bold"
                    slate
                    xs
                    faded
                    @click="openEditor(template.id)"
                  />
                  <Button
                    v-tooltip.top="t('FLEX_TEMPLATES.DELETE')"
                    icon="i-ph-trash-bold"
                    xs
                    ruby
                    faded
                    @click="deleteTemplate(template)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.settings-page,
.dark .settings-page {
  background: #ffffff !important;
}
.ws-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-desc {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-toolbar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}
.ws-search {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}
.ws-search:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  background: #fff !important;
}
.ws-search::placeholder {
  color: #9ca3af !important;
}
.ws-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Excon', system-ui, sans-serif;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.ws-btn:hover {
  background: #1f2937;
}
.ws-table-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
}
.ws-table {
  border-collapse: collapse !important;
  width: 100%;
  background: #ffffff !important;
}
.ws-thead {
  background: #f9fafb !important;
  border-bottom: 1px solid #e5e7eb !important;
}
.ws-th {
  padding: 10px 24px !important;
  text-align: left !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.05em !important;
  text-transform: uppercase !important;
  color: #374151 !important;
  background: #f9fafb !important;
}
.ws-th--right {
  text-align: right !important;
}
.ws-tr {
  border-bottom: 1px solid #f3f4f6 !important;
  background: #ffffff !important;
  transition: background 0.12s ease;
}
.ws-tr:last-child {
  border-bottom: none !important;
}
.ws-tr:hover {
  background: #fafafa !important;
}
.ws-td {
  padding: 14px 24px !important;
  font-size: 13px !important;
  color: #111827 !important;
  vertical-align: middle !important;
  background: #ffffff;
}
.ws-td--right {
  text-align: right !important;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
</style>
